import { useSEO, generateSchema } from '../../utils/seo';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const MembersPage = () => {
  const { i18n } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoshino-village-project.com';

  useSEO({
    title: i18n.language === 'ja' ? 'メンバー紹介 | Yoshino Village Project' : 'Members | Yoshino Village Project',
    description: i18n.language === 'ja' 
      ? '理想だけじゃない。リアルに動いている地域共創の現場へようこそ。吉野で活動するメンバーを紹介します。'
      : 'Not just ideals. Welcome to the real site of regional co-creation in action. Meet our team members working in Yoshino.',
    keywords: '吉野, メンバー, チーム, 地域活性化, プロジェクトリーダー, コミュニティマネージャー',
    canonical: `${siteUrl}/members`,
    schema: generateSchema.webpage(
      `${siteUrl}/members`,
      i18n.language === 'ja' ? 'メンバー紹介 | Yoshino Village Project' : 'Members | Yoshino Village Project',
      i18n.language === 'ja' 
        ? '理想だけじゃない。リアルに動いている地域共創の現場へようこそ。吉野で活動するメンバーを紹介します。'
        : 'Not just ideals. Welcome to the real site of regional co-creation in action. Meet our team members working in Yoshino.'
    ),
  });

  const members = [
    {
      name: '田中 健太',
      nameEn: 'Kenta Tanaka',
      role: 'プロジェクトリーダー',
      roleEn: 'Project Leader',
      description: '地域活性化の専門家として、15年以上の経験を持つ。吉野の魅力を最大限に引き出すプロジェクトの統括を担当。',
      descriptionEn: 'As a regional revitalization expert with over 15 years of experience, oversees projects that maximize Yoshino\'s appeal.',
      image: 'https://static.readdy.ai/image/502796c101e72168e087fe40585d8901/333cd6b617c692d4a71eea8ca932b89a.jpeg',
      bgColor: 'bg-pink-100',
    },
    {
      name: '佐藤 美咲',
      nameEn: 'Misaki Sato',
      role: 'コミュニティマネージャー',
      roleEn: 'Community Manager',
      description: '地域コミュニティの構築と運営を専門とし、住民と移住者の架け橋となる活動を推進。イベント企画も担当。',
      descriptionEn: 'Specializes in building and managing local communities, promoting activities that bridge residents and migrants. Also handles event planning.',
      image: 'https://public.readdy.ai/ai/img_res/3f96d0c1fda750b141ab8874bd8ea7d1.jpg',
      bgColor: 'bg-green-100',
    },
    {
      name: '山本 大輔',
      nameEn: 'Daisuke Yamamoto',
      role: '環境保全担当',
      roleEn: 'Environmental Conservation',
      description: '環境学の専門知識を活かし、吉野の自然保護と持続可能な環境づくりをリード。植樹活動の中心人物。',
      descriptionEn: 'Leveraging expertise in environmental science, leads nature conservation and sustainable environment creation in Yoshino. Central figure in tree planting activities.',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20man%20in%20his%2030s%20with%20confident%20smile%20wearing%20outdoor%20work%20clothing%2C%20nature%20enthusiast%20appearance%2C%20natural%20lighting%2C%20portrait%20photography%20style%2C%20simple%20clean%20background&width=300&height=400&seq=member-yamamoto&orientation=portrait',
      bgColor: 'bg-gray-100',
    },
    {
      name: '鈴木 愛',
      nameEn: 'Ai Suzuki',
      role: '広報・デザイン',
      roleEn: 'PR & Design',
      description: 'グラフィックデザイナーとして、プロジェクトのビジュアルアイデンティティを構築。SNS運営も担当。',
      descriptionEn: 'As a graphic designer, builds the project\'s visual identity. Also manages social media operations.',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20woman%20in%20her%2020s%20with%20creative%20smile%20wearing%20stylish%20casual%20clothing%2C%20artistic%20and%20modern%20appearance%2C%20natural%20lighting%2C%20portrait%20photography%20style%2C%20simple%20clean%20background&width=300&height=400&seq=member-suzuki&orientation=portrait',
      bgColor: 'bg-amber-100',
    },
    {
      name: '伊藤 誠',
      nameEn: 'Makoto Ito',
      role: '伝統文化継承担当',
      roleEn: 'Traditional Culture Succession',
      description: '吉野の伝統工芸や文化の保存と継承に尽力。ワークショップを通じて次世代への技術伝承を実践。',
      descriptionEn: 'Dedicated to preserving and passing on Yoshino\'s traditional crafts and culture. Practices skill transmission to the next generation through workshops.',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20man%20in%20his%2050s%20with%20wise%20smile%20wearing%20traditional%20japanese%20clothing%2C%20experienced%20craftsman%20appearance%2C%20natural%20lighting%2C%20portrait%20photography%20style%2C%20simple%20clean%20background&width=300&height=400&seq=member-ito&orientation=portrait',
      bgColor: 'bg-blue-100',
    },
    {
      name: '高橋 由美',
      nameEn: 'Yumi Takahashi',
      role: '教育プログラム担当',
      roleEn: 'Educational Programs',
      description: '教育者として、地域の子どもたちや若者向けの学習プログラムを企画・運営。人材育成に注力。',
      descriptionEn: 'As an educator, plans and operates learning programs for local children and youth. Focuses on human resource development.',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20woman%20in%20her%2040s%20with%20gentle%20smile%20wearing%20smart%20casual%20clothing%2C%20educator%20appearance%2C%20natural%20lighting%2C%20portrait%20photography%20style%2C%20simple%20clean%20background&width=300&height=400&seq=member-takahashi&orientation=portrait',
      bgColor: 'bg-purple-100',
    },
    {
      name: '中村 拓也',
      nameEn: 'Takuya Nakamura',
      role: 'イベント企画担当',
      roleEn: 'Event Planning',
      description: '地域イベントの企画から運営まで幅広く担当。参加者が楽しめる体験型プログラムを多数実施。',
      descriptionEn: 'Handles everything from planning to managing local events. Implements numerous experiential programs that participants can enjoy.',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20man%20in%20his%2030s%20with%20energetic%20smile%20wearing%20casual%20sporty%20clothing%2C%20dynamic%20appearance%2C%20natural%20lighting%2C%20portrait%20photography%20style%2C%20simple%20clean%20background&width=300&height=400&seq=member-nakamura&orientation=portrait',
      bgColor: 'bg-orange-100',
    },
    {
      name: '渡辺 さくら',
      nameEn: 'Sakura Watanabe',
      role: 'ボランティアコーディネーター',
      roleEn: 'Volunteer Coordinator',
      description: 'ボランティア活動の調整と管理を担当。参加者一人ひとりに寄り添ったサポートを提供。',
      descriptionEn: 'Handles coordination and management of volunteer activities. Provides personalized support to each participant.',
      image: 'https://readdy.ai/api/search-image?query=professional%20japanese%20woman%20in%20her%2020s%20with%20cheerful%20smile%20wearing%20casual%20comfortable%20clothing%2C%20approachable%20appearance%2C%20natural%20lighting%2C%20portrait%20photography%20style%2C%20simple%20clean%20background&width=300&height=400&seq=member-watanabe&orientation=portrait',
      bgColor: 'bg-pink-100',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="pt-32 pb-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm text-gray-500 font-light mb-3 tracking-widest">OUR TEAM</p>
            <h1 className="text-6xl font-black text-gray-900 mb-4">
              {i18n.language === 'ja' ? 'メンバー紹介' : 'Meet Our Team'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {i18n.language === 'ja' 
                ? '理想だけじゃない。リアルに動いている地域共創の現場へようこそ。'
                : 'Not just ideals. Welcome to the real site of regional co-creation in action.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-4 gap-6">
            {members.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`${member.bgColor} rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}
              >
                <div className="h-80">
                  <img
                    src={member.image}
                    alt={i18n.language === 'ja' ? member.name : member.nameEn}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-white p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {i18n.language === 'ja' ? member.name : member.nameEn}
                  </h3>
                  <p className="text-sm text-green-700 font-medium mb-3">
                    {i18n.language === 'ja' ? member.role : member.roleEn}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {i18n.language === 'ja' ? member.description : member.descriptionEn}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MembersPage;
